/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useContext, useRef } from 'react';
import {
  AutoSizer,
  List,
  CellMeasurer,
  CellMeasurerCache,
} from 'react-virtualized';
import { Button } from '@material-ui/core';
import { isSmallTouchDevice } from '@mummssoftware/utils/web';
import {
  PNGlobalContext,
  SET_NOTES_READY,
} from '@mummssoftware/global-context';
import { Note } from './Note';
import { NoteListWrapper, NoNoteDiv } from '../../components/layout/WrapperDiv';
import { Context } from '../../context';
import { useProgressNotes } from '../../hooks/useProgressNotes';
import { useStyles } from '../style/style';
import defaults from '../../translations/en.json';

const NoteList = () => {
  const {
    smallWidget,
    scrollToBottom,
    stopScroll,
    updatedNote,
    patient,
    agency,
    entityType,
    getAllProgressNotes,
    onWidgetReady,
    startLoadingTime,
    readOnly,
  } = useContext(Context);
  const { notesReady, dispatchPN } = useContext(PNGlobalContext);
  const progressNotes = useProgressNotes();

  const classes = useStyles();

  const maxNumber = 40;

  const hasNotes = !!(progressNotes && progressNotes.length);
  const isLargeData = hasNotes && progressNotes.length > maxNumber;
  const noteData = hasNotes ? progressNotes.slice(0, maxNumber) : progressNotes;

  const isIPad = isSmallTouchDevice();

  const [progressNotestoDisplay, setNoteData] = useState<
    mumms.progressNotebyEntity[]
  >(noteData);

  const [showLoadButton, setLoadButton] = useState<boolean>(false);
  const [showLoader, setLoader] = useState<boolean>(false);

  useEffect(() => {
    if (isLargeData) {
      setLoadButton(true);
    }
  }, [isLargeData]);

  useEffect(() => {
    if (showLoader) {
      if (progressNotestoDisplay.length === progressNotes.length) {
        setLoader(false);
      }
    }
  }, [showLoader, progressNotestoDisplay.length]);

  useEffect(() => {
    if (isLargeData && showLoadButton) {
      setNoteData(progressNotes.slice(0, maxNumber));
    } else {
      setNoteData(progressNotes);
    }
  }, [progressNotes, showLoadButton]);

  const loadMoreNote = () => {
    setLoader(true);
    setLoadButton(false);
    setTimeout(() => {
      setNoteData(progressNotes);
    }, 300);
  };

  const setPNReady = () => {
    if (!notesReady) {
      const loadingDuration = (Date.now() - startLoadingTime) / 1000;

      onWidgetReady && onWidgetReady('ProgressNotes' as mumms.ComponentName);
      console.log(
        '## PROGRESS NOTES & ORDERS WIDGET END ##',
        loadingDuration,
        's',
      );
      dispatchPN({
        type: SET_NOTES_READY,
        payload: true,
      });
    }
  };

  const getInitNotes = async () => {
    const init = true;
    const initNotes = await getAllProgressNotes(
      patient.patientNumber,
      agency,
      init,
    );
    if (initNotes && initNotes.length && initNotes.length > 200) {
      setTimeout(() => {
        setPNReady();
      }, 500);
    } else {
      setPNReady();
    }
  };

  useEffect(() => {
    // if entityType can be no notes
    if (!hasNotes && !entityType) {
      getInitNotes();
    }
  }, []);

  /**
   * React life cycle wait for tree panted and visible to user
   */
  const runAfterFramePaint = (callback: any) => {
    requestAnimationFrame(() => {
      const messageChannel = new MessageChannel();

      messageChannel.port1.onmessage = callback;
      messageChannel.port2.postMessage(undefined);
    });
  };

  useEffect(() => {
    if (hasNotes) {
      runAfterFramePaint(() => {
        // when the page has been fully painted and is visible to user
        stopScroll();
      });
    }
  }, [hasNotes]);

  const AlwaysScrollToBottom = () => {
    const elementRef = useRef();
    useEffect(() => {
      if (elementRef.current) {
        const target: any = elementRef.current;
        target.parentNode.scrollTop = target.offsetTop;
      }
    }, [elementRef.current]);
    return <div ref={elementRef as any} />;
  };

  const reverseNotes = hasNotes ? [...progressNotestoDisplay].reverse() : [];

  const cache = new CellMeasurerCache({
    fixedWidth: true,
    defaultHeight: 100,
  });

  const renderRow = ({ index, key, style, parent }: any) => (
    <CellMeasurer
      cache={cache}
      columnIndex={0}
      key={key}
      parent={parent}
      rowIndex={index}
    >
      <Note
        note={reverseNotes[index]}
        smallWidget={smallWidget}
        key={reverseNotes[index].id}
        style={style}
        readOnly={readOnly}
      />
    </CellMeasurer>
  );

  const getiPadView = () => (
    <AutoSizer style={{ height: '100%', width: '100%' }}>
      {({ height, width }: any) => (
        <>
          {hasNotes && (
            <List
              className="List"
              height={height}
              width={0.98 * width}
              rowCount={progressNotestoDisplay.length}
              rowHeight={cache.rowHeight}
              rowRenderer={renderRow}
              scrollToIndex={progressNotestoDisplay.length - 1}
            />
          )}
          {!notesReady && smallWidget && defaults.loadingNotes}
          {!hasNotes && smallWidget && notesReady && defaults.noNotes}
        </>
      )}
    </AutoSizer>
  );

  const getLargeViewport = () => (
    <>
      {showLoader && (
        <Button
          title="OK"
          size="small"
          variant="contained"
          color="primary"
          classes={{
            root: classes.loadAllButton,
          }}
        >
          {defaults.loadingAllData}
        </Button>
      )}
      {showLoadButton && (
        <Button
          title="OK"
          size="small"
          variant="contained"
          color="primary"
          onClick={loadMoreNote}
          classes={{
            root: classes.loadButton,
          }}
        >
          {defaults.loadMore}
        </Button>
      )}

      {hasNotes &&
        reverseNotes.map((note: mumms.progressNotebyEntity) => {
          const displayNote =
            // what is the logic of updatedNote
            updatedNote && updatedNote.id === note.id ? updatedNote : note;
          return (
            <Note
              note={displayNote}
              smallWidget={smallWidget}
              key={note.id}
              readOnly={readOnly}
            />
          );
        })}
      {!notesReady && smallWidget && defaults.loadingNotes}
      {!hasNotes && smallWidget && notesReady && (
        <NoNoteDiv>{defaults.noNotes}</NoNoteDiv>
      )}
      {scrollToBottom === 0 && <AlwaysScrollToBottom />}
    </>
  );

  return (
    <NoteListWrapper id="notes-wrapper">
      {/* {isIPad ? getiPadView() : getLargeViewport()} */}

      {getLargeViewport()}
    </NoteListWrapper>
  );
};

export default NoteList;
